import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowUpRight, CheckCircle2 } from 'lucide-react';
import { getCompanies } from '../lib/api';
import { CardShell, EmptyState, LoadingScreen, PageHero, staggerContainer, staggerItem } from '../components/common/BrandElements';

const CompaniesPage = () => {
  const { t, isRTL } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await getCompanies();
        setCompanies(res.data.filter(c => c.is_active));
      } catch (error) {
        console.error('Error loading companies:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-raad-porcelain" data-testid="companies-page">
      <PageHero
        kicker={t('شركاتنا', 'Our Companies')}
        title={t('مجموعة رعد القابضة', 'Raad Holding Group')}
        description={t('مجموعة شركات متخصصة تتحرك ضمن هوية واحدة: ثبات، ابتكار، ونمو صاعد.', 'A specialized group moving within one identity: stability, innovation, and upward growth.')}
      />

      <section className="pb-24">
        <div className="container mx-auto px-6">
          {companies.length === 0 ? (
            <EmptyState title={t('لا توجد شركات حالياً', 'No companies available')} />
          ) : (
            <motion.div variants={staggerContainer} initial="hidden" animate="show" className="grid grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-3">
              {companies.map((company) => {
                const services = (isRTL ? company.services_ar : company.services_en) || [];
                return (
                  <motion.div key={company.id} variants={staggerItem}>
                    <Link to={`/companies/${company.slug}`} className="block h-full group" data-testid={`company-card-${company.slug}`}>
                      <CardShell className="h-full p-7">
                        <div className="relative z-10 flex h-full flex-col">
                          <div className="mb-7 flex items-start justify-between gap-4">
                            <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-raad-mist">
                              {company.logo_url ? (
                                <img src={company.logo_url} alt={t(company.name_ar, company.name_en)} className="h-14 w-14 object-contain" />
                              ) : (
                                <span className="text-3xl font-black text-raad-teal">{(t(company.name_ar, company.name_en) || 'R')[0]}</span>
                              )}
                            </div>
                            <div className="rounded-full border border-raad-teal/20 p-3 text-raad-teal transition-colors group-hover:bg-raad-teal group-hover:text-white">
                              <ArrowUpRight className="h-5 w-5" />
                            </div>
                          </div>

                          <h2 className="text-2xl font-black text-raad-charcoal group-hover:text-raad-teal">
                            {t(company.name_ar, company.name_en)}
                          </h2>
                          <p className="mt-3 text-sm leading-7 text-raad-graphite/65 line-clamp-3">
                            {t(company.description_ar, company.description_en)}
                          </p>

                          {services.length > 0 && (
                            <div className="mt-6 flex flex-wrap gap-2">
                              {services.slice(0, 4).map((service, index) => (
                                <span key={`${service}-${index}`} className="inline-flex items-center gap-1.5 rounded-full bg-raad-mist px-3 py-1 text-xs font-bold text-raad-graphite/70">
                                  <CheckCircle2 className="h-3 w-3 text-raad-teal" />
                                  {service}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="mt-auto pt-7 text-sm font-black text-raad-teal">
                            {t('المزيد عن الشركة', 'Learn more')}
                          </div>
                        </div>
                      </CardShell>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CompaniesPage;
